import { useState } from "react";
import { EditContentDialog } from "@/components/EditContentDialog";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { formatTimezone } from "@/lib/utils";
import { convertStoredToDisplayFormat } from "@/lib/contentFormatting";

interface ScheduledPostsTabProps {
  posts: Array<{
    id: string;
    content: string;
    scheduled_for: string;
    timezone: string;
    created_at: string;
    images?: string[];
    video?: string;
  }>;
  onDeleteScheduled: (id: string) => void;
  onScheduledUpdated: () => void;
}

export const ScheduledPostsTab = ({
  posts,
  onDeleteScheduled,
  onScheduledUpdated,
}: ScheduledPostsTabProps) => {
  const [selectedPost, setSelectedPost] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<{
    id: string;
    content: string;
    scheduled_for: string;
    images?: string[];
    video?: string;
  } | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const handleDelete = async () => {
    if (selectedPost) {
      await onDeleteScheduled(selectedPost);
      setSelectedPost(null);
    }
  };

  const filteredPosts = posts
    .filter((post) => {
      const postDate = new Date(post.scheduled_for);
      const now = new Date();
      return showHistory ? postDate <= now : postDate > now;
    })
    .sort((a, b) => {
      // Sort by date, newest first
      return new Date(b.scheduled_for).getTime() - new Date(a.scheduled_for).getTime();
    });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2 mb-4">
        <span className="text-sm text-muted-foreground">Show history</span>
        <Switch
          checked={showHistory}
          onCheckedChange={setShowHistory}
        />
      </div>

      {filteredPosts.map((post) => (
        <div
          key={post.id}
          className="p-4 border rounded-lg bg-card shadow-sm hover:border-primary/50 transition-colors"
        >
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Scheduled for {format(new Date(post.scheduled_for), "PPP 'at' p")} ({formatTimezone(post.timezone)})
            </p>
            <div 
              className="prose prose-sm max-w-none text-sm whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ 
                __html: convertStoredToDisplayFormat(post.content) 
              }}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() =>
                  setEditingPost({
                    id: post.id,
                    content: post.content,
                    scheduled_for: post.scheduled_for,
                    images: post.images,
                    video: post.video,
                  })
                }
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSelectedPost(post.id)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        </div>
      ))}

      <AlertDialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this scheduled post.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editingPost && (
        <EditContentDialog
          open={!!editingPost}
          onOpenChange={() => setEditingPost(null)}
          initialContent={editingPost.content}
          contentType="scheduled"
          itemId={editingPost.id}
          onContentUpdated={() => {
            onScheduledUpdated();
            setEditingPost(null);
          }}
          scheduledFor={editingPost.scheduled_for}
          images={editingPost.images || []}
          video={editingPost.video || null} 
        />
      )}
    </div>
  );
};